import type {Journey} from 'redux/v1/journey/models';

import {useSplit} from '@travel-frontend/split-io';
import {format, parseISO} from 'date-fns';
import {lazy, Suspense, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {LayerPortal} from 'components/LayerPortal/LayerPortal';
import {useParseJourney} from 'components/TravelDetailsOverlay/hooks/useParseJourney';
import {config} from 'config';
import {SHOW_LCW_WIDGET} from 'consts';
import {useCurrentPage} from 'hooks/useCurrentPage';
import {getJourney} from 'redux/v1/journey/reducer';

const OmnichannelChatWidget = lazy(() =>
  // eslint-disable-next-line import/no-unresolved
  import('@travel-frontend/omnichannel-chat-widget/widget')
);

export const OmnichannelChat = () => {
  const currentPage = useCurrentPage();
  const journey: Journey = useSelector(getJourney);
  const parsedJourney = useParseJourney(journey);
  const isEnabled = useSplit(SHOW_LCW_WIDGET)?.treatment === 'on';
  const {omnichannelOrgId, omnichannelOrgUrl, omnichannelWidgetId} = config;

  const getLocalPrice = () => {
    if (journey.localPrices?.totalPrice) {
      const {amount, currency} = journey.localPrices.totalPrice;
      return `${amount / 100} ${currency}`;
    } else if (journey?.price) {
      return `${journey.price} €`;
    }
    return null;
  };

  const isMultiRoom = () => {
    const {parsedRooms} = parsedJourney.services.multiRooms;

    return parsedRooms.length > 1 || parsedRooms[0]?.roomCount > 1;
  };

  const customContext = useMemo(
    () => ({
      Herkunft: {
        isDisplayable: true,
        Value: currentPage === 'checkout' ? currentPage : 'confirmationpage',
      },
      Preis: {
        isDisplayable: true,
        Value: getLocalPrice(),
      },
      Veranstalter: {
        isDisplayable: true,
        Value: journey.tourOperator,
      },
      Reisebeginn: {
        isDisplayable: true,
        Value: format(parseISO(journey?.departureDate), 'dd.MM.yyyy'),
      },
      Reiseende: {
        isDisplayable: true,
        Value: format(parseISO(journey?.returnDate), 'dd.MM.yyyy'),
      },
      Verpflegung: {
        isDisplayable: true,
        Value: parsedJourney.services.catering,
      },
      Mehrzimmer: {
        isDisplayable: true,
        Value: isMultiRoom() ? 'Ja' : 'Nein',
      },
      Url: {
        isDisplayable: true,
        Value: journey.offersUrl,
      },
    }),
    [currentPage, journey, parsedJourney]
  );

  const chatButtonOffset = useMemo(
    () => ({
      top: null,
      left: null,
      right: 22,
      bottom: 76,
    }),
    []
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <LayerPortal>
      <Suspense>
        <OmnichannelChatWidget
          orgId={omnichannelOrgId}
          orgUrl={omnichannelOrgUrl}
          widgetId={omnichannelWidgetId}
          customContext={customContext}
          chatButtonOffset={chatButtonOffset}
        />
      </Suspense>
    </LayerPortal>
  );
};
